import type {PersistedTaskComposerScope} from './copilot-local-storage'

export interface SandboxScopeOrganization {
  login: string
  requiresSso?: boolean
  sandboxEnabled?: boolean
}

export type DefaultSandboxScope<O> = {kind: 'personal'} | {kind: 'organization'; organization: O}
export type DefaultSandboxScopeResult<O> = {status: 'not-ready'} | {status: 'ready'; scope: DefaultSandboxScope<O>}

export interface DefaultSandboxScopeInput<O extends SandboxScopeOrganization> {
  organizations: readonly O[] | undefined
  currentUserLogin?: string
  pendingSsoOrgLogin?: string | null
  pageOwner?: string
  launchOwner?: string
  persistedScope?: PersistedTaskComposerScope | null
  hasChosenScope?: boolean
  persistenceEnabled?: boolean
  lastContributedOrganizationLogin?: string
}

export function isOrganizationSandboxEnabled(organization: SandboxScopeOrganization): boolean {
  return organization.sandboxEnabled !== false
}

export function isOrganizationEligibleForDefaultScope(organization: SandboxScopeOrganization): boolean {
  return !organization.requiresSso && isOrganizationSandboxEnabled(organization)
}

/**
 * A persisted organization removed, revoked, disabled for Sandbox, or requiring
 * SSO falls back to Personal so an unresolved handshake cannot silently pin scope.
 */
export function resolvePersistedScope<O extends SandboxScopeOrganization>(
  persisted: PersistedTaskComposerScope | null | undefined,
  availableOrganizations: readonly O[],
): DefaultSandboxScope<O> {
  if (persisted?.kind !== 'organization') return {kind: 'personal'}
  const match = availableOrganizations.find(org => org.login === persisted.login)
  return match && isOrganizationEligibleForDefaultScope(match)
    ? {kind: 'organization', organization: match}
    : {kind: 'personal'}
}

/**
 * Resolves a Sandbox default without reading storage. Undefined organizations
 * means the account has not loaded; callers whose loaded payload omits the
 * organization list pass an empty array instead.
 */
export function resolveDefaultSandboxScope<O extends SandboxScopeOrganization>({
  organizations,
  currentUserLogin,
  pendingSsoOrgLogin,
  pageOwner,
  launchOwner,
  persistedScope,
  hasChosenScope,
  persistenceEnabled,
  lastContributedOrganizationLogin,
}: DefaultSandboxScopeInput<O>): DefaultSandboxScopeResult<O> {
  if (!organizations) return {status: 'not-ready'}

  const findOrganization = (login: string) => organizations.find(org => org.login.toLowerCase() === login.toLowerCase())

  if (pendingSsoOrgLogin) {
    const restoredOrganization = findOrganization(pendingSsoOrgLogin)
    if (restoredOrganization && isOrganizationEligibleForDefaultScope(restoredOrganization)) {
      return {status: 'ready', scope: {kind: 'organization', organization: restoredOrganization}}
    }
  }

  if (pageOwner !== undefined) {
    if (currentUserLogin && pageOwner.toLowerCase() === currentUserLogin.toLowerCase()) {
      return {status: 'ready', scope: {kind: 'personal'}}
    }
    const pageOrganization = findOrganization(pageOwner)
    if (pageOrganization && !isOrganizationSandboxEnabled(pageOrganization)) {
      return {status: 'ready', scope: {kind: 'personal'}}
    }
    if (pageOrganization && isOrganizationEligibleForDefaultScope(pageOrganization)) {
      return {status: 'ready', scope: {kind: 'organization', organization: pageOrganization}}
    }
  } else if (launchOwner) {
    if (currentUserLogin && launchOwner.toLowerCase() === currentUserLogin.toLowerCase()) {
      return {status: 'ready', scope: {kind: 'personal'}}
    }
    const launchOrganization = findOrganization(launchOwner)
    if (launchOrganization) {
      return {
        status: 'ready',
        scope: isOrganizationSandboxEnabled(launchOrganization)
          ? {kind: 'organization', organization: launchOrganization}
          : {kind: 'personal'},
      }
    }
  }

  if (persistenceEnabled) {
    if (persistedScope) {
      return {status: 'ready', scope: resolvePersistedScope(persistedScope, organizations)}
    }
    if (!pendingSsoOrgLogin && !hasChosenScope && lastContributedOrganizationLogin) {
      const contributedOrganization = findOrganization(lastContributedOrganizationLogin)
      if (contributedOrganization && isOrganizationEligibleForDefaultScope(contributedOrganization)) {
        return {status: 'ready', scope: {kind: 'organization', organization: contributedOrganization}}
      }
    }
  }
  return {status: 'ready', scope: {kind: 'personal'}}
}
